import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session, User } from '@supabase/supabase-js';
import type { Database, SubscriptionType } from '@upcaret/supabase-types';
import { getPublicEnvVar, isProduction } from '@upcaret/env-utils';

type BillingProvider = 'stripe' | 'apple' | 'google_play' | 'unknown';
type SubscriptionManageSurface = 'web' | 'ios' | 'android' | 'unknown';
type SubscriptionAccessInfo = {
  provider: BillingProvider;
  manageIn: SubscriptionManageSurface;
  manageLabel: string;
  planName: 'monthly' | 'annual' | 'unknown';
  trialDaysRemaining: number;
};

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  subscription: SubscriptionType | null;
  subscriptionAccess: SubscriptionAccessInfo | null;
  hasBillingHistory: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasValidSubscription: boolean;
  error: unknown;
  refreshSession: () => Promise<Session | null>;
  startTrial: (lookupKey?: 'standard_monthly' | 'standard_annual') => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
};

const SESSION_QUERY_KEY = ['auth', 'session'] as const;
const SUBSCRIPTION_QUERY_KEY = ['auth', 'subscription'] as const;
const BILLING_HISTORY_QUERY_KEY = ['auth', 'billing-history'] as const;
type EntitlementCurrentType = Database['api']['Views']['entitlement_current']['Row'];
const posthogEnabled =
  Boolean(
    getPublicEnvVar('VITE_CENTLY_PUBLIC_POSTHOG_KEY') ||
      getPublicEnvVar('VITE_PUBLIC_POSTHOG_KEY'),
  ) && isProduction();

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

const PROMPT_CACHE_REFRESH_KEY = 'cently_prompt_cache_last_refresh';
// Don't fire the background refresh more than once per hour per browser tab
const PROMPT_CACHE_REFRESH_INTERVAL_MS = 60 * 60 * 1000;

function shouldRefreshPromptCache(): boolean {
  try {
    const last = sessionStorage.getItem(PROMPT_CACHE_REFRESH_KEY);
    if (!last) return true;
    return Date.now() - parseInt(last, 10) > PROMPT_CACHE_REFRESH_INTERVAL_MS;
  } catch {
    return true;
  }
}

function markPromptCacheRefreshTriggered(): void {
  try {
    sessionStorage.setItem(PROMPT_CACHE_REFRESH_KEY, String(Date.now()));
  } catch {
    // Ignore — sessionStorage may be unavailable in restricted contexts
  }
}

async function syncPostHogIdentity(user: User | null): Promise<void> {
  if (!isBrowser() || !posthogEnabled) return;

  try {
    const posthog = (await import('posthog-js')).default;
    if (user?.id) {
      const props: Record<string, string> = { platform: 'web' };
      if (user.email) props.email = user.email;
      posthog.identify(user.id, props);
      return;
    }
    posthog.reset();
  } catch (error) {
    console.error('[AuthProvider] PostHog identity sync failed', error);
  }
}

function hasValidSubscription(subscription: SubscriptionType | null): boolean {
  if (!subscription) return false;
  return ['active', 'trialing'].includes(subscription.status);
}

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [localSession, setLocalSession] = useState<Session | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const timezoneHydratedUserIdRef = useRef<string | null>(null);

  const sessionQuery = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<Session | null> => {
      if (!isBrowser()) return null;
      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session ?? null;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: isBrowser(),
  });

  // Sync localSession with sessionQuery.data on initial load
  useEffect(() => {
    if (sessionQuery.data !== undefined) {
      setLocalSession(sessionQuery.data);
      setIsInitialLoading(false);
    }
  }, [sessionQuery.data]);

  const subscriptionQuery = useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: async (): Promise<SubscriptionType | null> => {
      if (!isBrowser()) return null;
      const session = localSession;
      if (!session) return null;

      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data, error } = await supabase
        .schema('api')
        .from('entitlement_current')
        .select()
        .eq('app_id', 'cently')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) {
        console.error('[AuthProvider] Subscription fetch failed', error);
      }

      return mapEntitlementToSubscription(data);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: isBrowser() && !!localSession,
  });

  const billingHistoryQuery = useQuery({
    queryKey: BILLING_HISTORY_QUERY_KEY,
    queryFn: async (): Promise<boolean> => {
      if (!isBrowser()) return false;
      const session = localSession;
      if (!session) return false;

      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data, error } = await supabase
        .schema('api')
        .rpc('has_billing_history', { p_app_id: 'cently' });

      if (error) {
        console.error('[AuthProvider] Billing history fetch failed', error);
        return false;
      }

      return Boolean(data);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: isBrowser() && !!localSession,
  });

  const startTrialMutation = useMutation({
    mutationFn: async (
      lookupKey: 'standard_monthly' | 'standard_annual' = 'standard_monthly',
    ) => {
      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data, error } = await supabase.functions.invoke(
        'stripe-create-checkout-session',
        {
          body: {
            session_type: 'trial',
            lookup_key: lookupKey,
            stripe_account: 'cently',
          },
        },
      );

      const payload = data as
        | {
            success?: boolean;
            error?: string;
            already_exists?: boolean;
            subscription_id?: string;
          }
        | null
        | undefined;
      if (error || payload?.error || payload?.success === false) {
        throw new Error(
          payload?.error || error?.message || 'Unable to start your trial right now.',
        );
      }

      return payload;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_HISTORY_QUERY_KEY });
    },
  });

  const signOutMutation = useMutation({
    mutationFn: async () => {
      const { supabase, signOutSafely } = await import('@upcaret/supabase-types/client');
      await signOutSafely(supabase);
    },
    onSuccess: () => {
      setLocalSession(null);
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      queryClient.setQueryData(SUBSCRIPTION_QUERY_KEY, null);
      queryClient.setQueryData(BILLING_HISTORY_QUERY_KEY, false);
      queryClient.clear();
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      const session = localSession;
      if (!session?.user?.id) {
        throw new Error('You must be signed in to delete your account.');
      }

      const { supabase, signOutSafely } = await import('@upcaret/supabase-types/client');
      const { error } = await supabase.functions.invoke('delete-account', {
        method: 'POST',
        body: {
          user_id: session.user.id.toLowerCase(),
          confirm: true,
        },
      });
      if (error) throw error;

      // Best effort: token may already be invalid after account deletion.
      await signOutSafely(supabase);
    },
    onSuccess: () => {
      setLocalSession(null);
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      queryClient.setQueryData(SUBSCRIPTION_QUERY_KEY, null);
      queryClient.setQueryData(BILLING_HISTORY_QUERY_KEY, false);
      queryClient.clear();
    },
  });

  useEffect(() => {
    if (!isBrowser()) return;

    let unsubscribe: (() => void) | undefined;

    (async () => {
      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data } = supabase.auth.onAuthStateChange(
        async (event, nextSession) => {
          setLocalSession(nextSession ?? null);
          queryClient.setQueryData(SESSION_QUERY_KEY, nextSession ?? null);

          if (nextSession) {
            queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: BILLING_HISTORY_QUERY_KEY });
          } else {
            queryClient.setQueryData(SUBSCRIPTION_QUERY_KEY, null);
            queryClient.setQueryData(BILLING_HISTORY_QUERY_KEY, false);
          }
          await syncPostHogIdentity(nextSession?.user ?? null);
          setIsInitialLoading(false);

          // Kick off a background cache refresh whenever a session starts.
          // TOKEN_REFRESHED fires every hour so we skip it; SIGNED_IN and
          // INITIAL_SESSION are the only events that indicate a fresh page load
          // or an actual login. The edge function checks the kv_cache TTL itself
          // and does nothing if all prompts are still fresh.
          if (
            nextSession &&
            (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
            shouldRefreshPromptCache()
          ) {
            markPromptCacheRefreshTriggered();
            supabase.functions
              .invoke('refresh-prompt-cache', { method: 'POST' })
              .catch(() => {
                // Fire and forget — errors are logged server-side
              });
          }
        },
      );
      unsubscribe = () => data.subscription.unsubscribe();
    })().catch((err) => {
      console.error('[AuthProvider] Failed to attach auth state listener', err);
    });

    return () => {
      unsubscribe?.();
    };
  }, [queryClient]);

  useEffect(() => {
    void syncPostHogIdentity(localSession?.user ?? null);
  }, [localSession?.user?.id, localSession?.user?.email]);

  useEffect(() => {
    if (!isBrowser()) return;
    const userId = localSession?.user?.id ?? null;
    if (!userId) {
      timezoneHydratedUserIdRef.current = null;
      return;
    }
    if (timezoneHydratedUserIdRef.current === userId) return;

    timezoneHydratedUserIdRef.current = userId;
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    (async () => {
      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data, error } = await supabase
        .schema('api')
        .from('user_preferences')
        .select('timezone')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AuthProvider] Failed to read user timezone preference', error);
        return;
      }

      if (data?.timezone) return;

      const { error: upsertError } = await supabase
        .schema('api')
        .from('user_preferences')
        .upsert(
          {
            user_id: userId,
            timezone: browserTimezone,
          },
          { onConflict: 'user_id' },
        );

      if (upsertError) {
        console.error('[AuthProvider] Failed to initialize user timezone preference', upsertError);
      }
    })().catch((error) => {
      console.error('[AuthProvider] Failed to initialize user timezone preference', error);
    });
  }, [localSession?.user?.id]);

  const refreshSession = useCallback(async () => {
    if (!isBrowser()) return null;
    const { supabase } = await import('@upcaret/supabase-types/client');
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    setLocalSession(data.session ?? null);
    queryClient.setQueryData(SESSION_QUERY_KEY, data.session ?? null);
    return data.session ?? null;
  }, [queryClient]);

  const startTrial = useCallback(
    async (
      lookupKey: 'standard_monthly' | 'standard_annual' = 'standard_monthly',
    ) => {
      await startTrialMutation.mutateAsync(lookupKey);
    },
    [startTrialMutation],
  );

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  const deleteAccount = useCallback(async () => {
    await deleteAccountMutation.mutateAsync();
  }, [deleteAccountMutation]);

  const value = useMemo<AuthContextValue>(() => {
    const session = localSession;
    const user = session?.user ?? null;
    const subscription = subscriptionQuery.data ?? null;
    const subscriptionAccess = toSubscriptionAccessInfo(subscription);
    const hasBillingHistory = billingHistoryQuery.data ?? false;
    const isAuthenticated = !!session;
    const isLoading =
      isInitialLoading ||
      (isAuthenticated &&
        (subscriptionQuery.isLoading || billingHistoryQuery.isLoading));

    return {
      session,
      user,
      subscription,
      subscriptionAccess,
      hasBillingHistory,
      isAuthenticated,
      isLoading,
      hasValidSubscription: hasValidSubscription(subscription),
      error: sessionQuery.error || subscriptionQuery.error,
      refreshSession,
      startTrial,
      signOut,
      deleteAccount,
    };
  }, [
    localSession,
    isInitialLoading,
    subscriptionQuery.data,
    subscriptionQuery.isLoading,
    billingHistoryQuery.data,
    billingHistoryQuery.isLoading,
    sessionQuery.error,
    subscriptionQuery.error,
    refreshSession,
    startTrial,
    signOut,
    deleteAccount,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

function mapEntitlementToSubscription(
  entitlement: EntitlementCurrentType | null,
): SubscriptionType | null {
  if (!entitlement?.status || !entitlement.current_period_end) {
    return null;
  }
  return {
    id: entitlement.id ?? 0,
    user_id: entitlement.user_id ?? '',
    stripe_subscription_id: entitlement.provider_subscription_id ?? '',
    status: entitlement.status,
    price_id: entitlement.plan_code ?? 'unknown_plan',
    cancel_at_period_end: entitlement.cancel_at_period_end ?? false,
    current_period_start:
      entitlement.current_period_start ?? entitlement.current_period_end,
    current_period_end: entitlement.current_period_end,
    created_at: entitlement.created_at ?? new Date(0).toISOString(),
    updated_at: entitlement.updated_at ?? new Date(0).toISOString(),
    trial_end: entitlement.trial_end ?? null,
    metadata: {
      ...(entitlement.metadata && typeof entitlement.metadata === 'object'
        ? entitlement.metadata
        : {}),
      provider: entitlement.provider ?? null,
      plan_code: entitlement.plan_code ?? null,
      manage_in: entitlement.manage_in ?? null,
      manage_label: entitlement.manage_label ?? null,
      plan_name: entitlement.plan_name ?? null,
      trial_days_remaining: entitlement.trial_days_remaining ?? null,
      offer_phase: entitlement.offer_phase ?? null,
    },
    stripe_account: entitlement.app_id ?? 'cently',
  };
}

function toSubscriptionAccessInfo(
  subscription: SubscriptionType | null,
): SubscriptionAccessInfo | null {
  if (!subscription) return null;
  const provider = getProvider(subscription);
  const manageIn = getMetadataString(subscription, 'manage_in');
  const manageLabel = getMetadataString(subscription, 'manage_label');
  const planNameRaw = getMetadataString(subscription, 'plan_name');
  const trialDaysRemainingRaw = getMetadataNumber(
    subscription,
    'trial_days_remaining',
  );

  return {
    provider,
    manageIn:
      manageIn === 'web' ||
      manageIn === 'ios' ||
      manageIn === 'android' ||
      manageIn === 'unknown'
        ? manageIn
        : 'unknown',
    manageLabel:
      typeof manageLabel === 'string' && manageLabel.length > 0
        ? manageLabel
        : 'Unknown',
    planName:
      planNameRaw === 'monthly' || planNameRaw === 'annual' || planNameRaw === 'unknown'
        ? planNameRaw
        : 'unknown',
    trialDaysRemaining:
      typeof trialDaysRemainingRaw === 'number'
        ? Math.max(0, Math.floor(trialDaysRemainingRaw))
        : 0,
  };
}

function getProvider(subscription: SubscriptionType): BillingProvider {
  const provider = getMetadataString(subscription, 'provider');
  if (provider === 'stripe' || provider === 'apple' || provider === 'google_play') {
    return provider;
  }
  return 'unknown';
}

function getMetadataString(
  subscription: SubscriptionType,
  key: string,
): string | undefined {
  if (!subscription.metadata || typeof subscription.metadata !== 'object') {
    return undefined;
  }
  const value = (subscription.metadata as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function getMetadataNumber(
  subscription: SubscriptionType,
  key: string,
): number | undefined {
  if (!subscription.metadata || typeof subscription.metadata !== 'object') {
    return undefined;
  }
  const value = (subscription.metadata as Record<string, unknown>)[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}
